"use client";

import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';

interface Widget {
  image: string;
  name: string;
}

const widgets: Widget[] = [
  { image: "/players/widget_1.avif", name: "arturo coello" },
  { image: "/players/widget_2.avif", name: "Austin Tapia" },
  { image: "/players/widget_3.avif", name: "alejandro galan" },
  { image: "/players/widget_4.avif", name: "federico chingotto" },
  { image: "/players/widget_5.avif", name: "franco stupaczuk" },
  { image: "/players/widget_6.avif", name: "juan lebron" },
  { image: "/players/widget_7.avif", name: "jorge nieto" },
  { image: "/players/widget_8.avif", name: "miguel yanguas" },
  { image: "/players/widget_9.avif", name: "francisco navarro" },
  { image: "/players/widget_10.avif", name: "leandro augsburger" },
];

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

const createSVG = (type: string, attrs: Record<string, string | number> = {}): SVGElement => {
  const svgElement = document.createElementNS("http://www.w3.org/2000/svg", type);
  Object.entries(attrs).forEach(([k, v]) => svgElement.setAttribute(k, String(v)));
  return svgElement;
};

export default function WidgetSpinner() {
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [currentWidget, setCurrentWidget] = useState<string>("");
  
  const animationRef = useRef({
    centerX: 0,
    centerY: 0,
    outerRadius: 0,
    currentIndicatorRotation: 0,
    targetIndicatorRotation: 0,
    currentSpinnerRotation: 0,
    targetSpinnerRotation: 0,
    lastTime: performance.now(),
    lastSegmentIndex: -1,
    animationId: 0,
    lastScrollY: 0,
    segments: [] as SVGElement[],
    indicator: null as SVGElement | null,
    scrollThrottleId: 0,
  });

  useEffect(() => {
    const createWidgetSpinner = () => {
      if (!containerRef.current || !sectionRef.current) return;

      const sectionRect = sectionRef.current.getBoundingClientRect();
      const sectionWidth = sectionRef.current.offsetWidth;
      const sectionHeight = sectionRef.current.offsetHeight;
      
      const viewportSize = Math.min(sectionWidth, sectionHeight);
      const outerRadius = viewportSize * 0.35;
      const innerRadius = viewportSize * 0.22;
      const centerX = sectionWidth / 2;
      const centerY = sectionHeight / 2;

      animationRef.current.outerRadius = outerRadius;
      animationRef.current.centerX = centerX;
      animationRef.current.centerY = centerY;

      const svg = createSVG("svg", { id: "widget-svg" }) as SVGSVGElement;
      svg.classList.add('absolute', 'top-0', 'left-0', 'w-full', 'h-full');
      
      const defs = createSVG("defs");
      svg.appendChild(defs);

      const anglePerSegment = (2 * Math.PI) / widgets.length;

      for (let i = 0; i < widgets.length; i++) {
        const startAngle = i * anglePerSegment - Math.PI / 2;
        const endAngle = (i + 1) * anglePerSegment - Math.PI / 2;
        const midAngle = (startAngle + endAngle) / 2;

        const clipPath = createSVG("clipPath", { id: `clip-${i}` });
        const path = `M ${centerX + outerRadius * Math.cos(startAngle)} ${
          centerY + outerRadius * Math.sin(startAngle)
        } A ${outerRadius} ${outerRadius} 0 0 1 ${
          centerX + outerRadius * Math.cos(endAngle)
        } ${centerY + outerRadius * Math.sin(endAngle)} L ${
          centerX + innerRadius * Math.cos(endAngle)
        } ${
          centerY + innerRadius * Math.sin(endAngle)
        } A ${innerRadius} ${innerRadius} 0 0 0 ${
          centerX + innerRadius * Math.cos(startAngle)
        } ${centerY + innerRadius * Math.sin(startAngle)} Z`;

        clipPath.appendChild(createSVG("path", { d: path }));
        defs.appendChild(clipPath);

        const g = createSVG("g", {
          "clip-path": `url(#clip-${i})`,
          "data-segment": String(i),
        });

        const segmentRadius = (innerRadius + outerRadius) / 2;
        const segmentX = centerX + Math.cos(midAngle) * segmentRadius;
        const segmentY = centerY + Math.sin(midAngle) * segmentRadius;

        const arcLength = outerRadius * anglePerSegment;
        const imgWidth = arcLength * 1.25;
        const imgHeight = (outerRadius - innerRadius) * 1.25;
        const rotation = (midAngle * 180) / Math.PI + 90;

        const image = createSVG("image", {
          href: widgets[i].image,
          width: imgWidth,
          height: imgHeight,
          x: segmentX - imgWidth / 2,
          y: segmentY - imgHeight / 2,
          preserveAspectRatio: "xMidYMid slice",
          transform: `rotate(${rotation} ${segmentX} ${segmentY})`,
        });

        g.appendChild(image);
        svg.appendChild(g);
      }

      const widgetIndicator = createSVG("line", {
        id: "widget-indicator",
        x1: centerX,
        y1: centerY - innerRadius * 0.85,
        x2: centerX,
        y2: centerY - outerRadius * 1.05,
      });
      widgetIndicator.setAttribute('stroke', '#ffff2b');
      widgetIndicator.setAttribute('stroke-width', '3');
      widgetIndicator.setAttribute('stroke-linecap', 'round');
      svg.appendChild(widgetIndicator);

      // Cache segments and indicator for performance
      animationRef.current.segments = Array.from(svg.querySelectorAll("[data-segment]"));
      animationRef.current.indicator = svg.querySelector("#widget-indicator");

      containerRef.current.appendChild(svg);
      svgRef.current = svg;
    };

    const updateContent = () => {
      const { currentIndicatorRotation, currentSpinnerRotation } = animationRef.current;
      const relativeRotation = (((currentIndicatorRotation - currentSpinnerRotation) % 360) + 360) % 360;
      const segmentIndex = Math.floor(relativeRotation / 36) % widgets.length;

      if (segmentIndex !== animationRef.current.lastSegmentIndex) {
        animationRef.current.lastSegmentIndex = segmentIndex;
        setCurrentWidget(widgets[segmentIndex].name);

        if (previewRef.current) {
          // Reuse existing image elements instead of creating new ones
          const allImages = previewRef.current.querySelectorAll("img");
          let targetImg = null;

          // Reuse last image if exists
          if (allImages.length > 0) {
            targetImg = allImages[allImages.length - 1] as HTMLImageElement;
            targetImg.src = widgets[segmentIndex].image;
            targetImg.alt = widgets[segmentIndex].name;
          } else {
            targetImg = document.createElement("img");
            targetImg.src = widgets[segmentIndex].image;
            targetImg.alt = widgets[segmentIndex].name;
            targetImg.className = "absolute top-0 left-0 w-full h-full object-cover saturate-0";
            previewRef.current.appendChild(targetImg);
          }

          gsap.set(targetImg, { opacity: 0 });
          gsap.to(targetImg, { opacity: 1, duration: 0.15, ease: "power2.out" });

          // Clean up old images but keep a small buffer
          if (allImages.length > 2) {
            for (let i = 0; i < allImages.length - 2; i++) {
              previewRef.current.removeChild(allImages[i]);
            }
          }
        }
      }
    };

    const animate = () => {
      const currentTime = performance.now();
      let deltaTime = (currentTime - animationRef.current.lastTime) / 1000;
      animationRef.current.lastTime = currentTime;
      deltaTime = Math.min(deltaTime, 0.1);

      animationRef.current.targetIndicatorRotation += 18 * deltaTime;
      animationRef.current.targetSpinnerRotation -= 18 * 0.25 * deltaTime;

      animationRef.current.currentIndicatorRotation = lerp(
        animationRef.current.currentIndicatorRotation,
        animationRef.current.targetIndicatorRotation,
        0.1
      );
      animationRef.current.currentSpinnerRotation = lerp(
        animationRef.current.currentSpinnerRotation,
        animationRef.current.targetSpinnerRotation,
        0.1
      );

      const rotationStr = `rotate(${animationRef.current.currentIndicatorRotation % 360} ${animationRef.current.centerX} ${animationRef.current.centerY})`;
      
      // Use cached indicator reference
      if (animationRef.current.indicator) {
        animationRef.current.indicator.setAttribute("transform", rotationStr);
      }

      const spinnerRotationStr = `rotate(${animationRef.current.currentSpinnerRotation % 360} ${animationRef.current.centerX} ${animationRef.current.centerY})`;
      
      // Use cached segments for better performance
      animationRef.current.segments.forEach((seg) => {
        seg.setAttribute("transform", spinnerRotationStr);
      });

      updateContent();
      animationRef.current.animationId = requestAnimationFrame(animate);
    };

    createWidgetSpinner();
    animate();

    const handleScroll = () => {
      // Cancel previous throttle
      if (animationRef.current.scrollThrottleId) {
        clearTimeout(animationRef.current.scrollThrottleId);
      }

      const scrollY = window.scrollY;
      const delta = (scrollY - animationRef.current.lastScrollY) * 0.5;
      animationRef.current.lastScrollY = scrollY;
      
      animationRef.current.targetIndicatorRotation += delta;
      animationRef.current.targetSpinnerRotation -= delta;
    };

    const handleResize = () => {
      if (svgRef.current) {
        svgRef.current.remove();
        svgRef.current = null;
      }
      animationRef.current.segments = [];
      animationRef.current.indicator = null;
      createWidgetSpinner();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationRef.current.animationId);
      if (animationRef.current.scrollThrottleId) {
        clearTimeout(animationRef.current.scrollThrottleId);
      }
      if (svgRef.current) {
        svgRef.current.remove();
      }
      animationRef.current.segments = [];
      animationRef.current.indicator = null;
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full h-screen overflow-hidden bg-black">
      <div ref={previewRef} className="absolute w-full h-full" />
      <div ref={containerRef} className="absolute w-full h-full" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 uppercase text-xs font-medium px-2 py-1 bg-yel rounded-sm z-10">
        {currentWidget}
      </div>
    </section>
  );
}